/**
 * pdp-discount-price.js
 * Checks the Storefront API (via the shared StorefrontClient queue + unified
 * cache) for automatic discounts on the current PDP variant and updates the
 * price display if a discount is found.
 */
(function () {
  // Variant clicks are gated to one Storefront lookup per 1.1s (trailing
  // edge): rapid toggling queries only the variant the user settles on.
  // Cached variants render instantly regardless of the gate.
  var MIN_INTERVAL_MS = 1100;

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Adapt the client's info shape (dollars) to this renderer's cents shape.
  // Returns { sale, original, title } when discounted, null otherwise.
  function toRenderData(info) {
    if (!info || info.unresolved || !info.hasDiscount) return null;
    var sale = Math.round(info.finalPrice * 100);
    var original = Math.round(info.originalPrice * 100);
    if (sale >= original) return null;
    return { sale: sale, original: original, title: info.discountTitle || '' };
  }

  function renderDiscount(data, sectionId) {
    if (!data) return;

    var salePriceCents = data.sale;
    var strikethroughCents = data.original;

    var priceBlock = document.getElementById('price-' + sectionId);
    if (!priceBlock) return;

    var priceContainer = priceBlock.querySelector('.price__container');
    if (!priceContainer) return;

    // Update price display
    var regularDiv = priceContainer.querySelector('.price__regular');
    var saleDiv = priceContainer.querySelector('.price__sale');

    if (regularDiv) regularDiv.style.display = 'none';
    if (saleDiv) {
      saleDiv.style.display = 'flex';
      saleDiv.style.alignItems = 'baseline';
      // wrap: when the price + strikethrough + Save badge don't fit (narrow
      // viewports), the badge drops to its own line instead of cropping.
      saleDiv.style.flexWrap = 'wrap';
      saleDiv.style.gap = '6px';

      var oldPriceEl = saleDiv.querySelector('.price-item--regular');
      var salePriceEl = saleDiv.querySelector('.price-item--sale');

      if (oldPriceEl) oldPriceEl.textContent = formatMoney(strikethroughCents);
      if (salePriceEl) salePriceEl.textContent = formatMoney(salePriceCents);
    }

    // Show save badge inline, to the right of the struck-through price.
    var savePercent = Math.round((strikethroughCents - salePriceCents) / strikethroughCents * 100);
    var existingBadge = document.querySelector('.pdp-badge--save');
    if (savePercent > 0) {
      if (existingBadge) {
        existingBadge.querySelector('span').textContent = 'Save ' + savePercent + '%';
      } else if (saleDiv) {
        var badge = document.createElement('div');
        badge.className = 'pdp-badge pdp-badge--save';
        badge.style.alignSelf = 'center';
        badge.style.flexShrink = '0';
        badge.style.whiteSpace = 'nowrap';
        badge.innerHTML = '<span>Save ' + savePercent + '%</span>';
        saleDiv.appendChild(badge);
      }
    }

    // Update price wrapper class
    var priceWrapper = priceBlock.querySelector('.price');
    if (priceWrapper) {
      priceWrapper.classList.add('price--on-sale');
    }
  }

  var lastFetchAt = 0;
  var pendingTimer = null;
  // Latest variant the user selected — any timer or response for an older
  // variant must NOT touch the price display.
  var currentVariantId = null;

  function fetchAndRender(variantId, sectionId) {
    lastFetchAt = Date.now();
    window.StorefrontClient.getDiscounts([variantId]).then(function (map) {
      if (String(variantId) !== String(currentVariantId)) return; // stale response
      var info = map.get(String(variantId));
      // null = undetermined (throttled/error) — uncached, retries next time
      renderDiscount(toRenderData(info), sectionId);
    }).catch(function () {});
  }

  function checkAndUpdatePrice(variantId, sectionId) {
    if (!variantId || !window.StorefrontClient) return;
    currentVariantId = variantId;
    if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }

    // Serve from the shared cache when any surface already resolved this
    // variant (PLP card, header popover, earlier PDP visit).
    var cached = window.StorefrontClient.cacheGet(variantId);
    if (cached) {
      renderDiscount(toRenderData(cached), sectionId);
      return;
    }

    // Trailing-edge gate: only the latest selected variant is queried, no
    // sooner than MIN_INTERVAL_MS after the previous lookup.
    var wait = Math.max(0, lastFetchAt + MIN_INTERVAL_MS - Date.now());
    pendingTimer = setTimeout(function () {
      pendingTimer = null;
      fetchAndRender(variantId, sectionId);
    }, wait);
  }

  // --- Init ---
  function init() {
    var productInfo = document.querySelector('product-info[data-section]');
    if (!productInfo) return;

    var sectionId = productInfo.dataset.section;

    // Get initial variant ID
    var variantInput = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');
    if (variantInput) {
      checkAndUpdatePrice(variantInput.value, sectionId);
    }

    // Listen for variant changes
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event.data && event.data.variant;
        if (variant) {
          // Small delay to let the server-rendered price update first
          setTimeout(function () {
            checkAndUpdatePrice(variant.id, sectionId);
          }, 100);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
